// keyrd_console/src/components/Dashboard.js
import React, { useEffect, useState } from "react";
import { buildKeyrdHeaders } from "../api/headers";

function toNumberOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function Dashboard({auth, rdnToken, authToken, rdnContext, currentRdn}) {
  const { baseUrl, token, authorized, registryVersion, registryHash } = auth;
  const pcUatEffectiveRdnToken = token || rdnToken || authToken || "";
  const pcUatHasRdnContext = Boolean(rdnContext || currentRdn);
  const pcUatDashboardAuthorized = Boolean(authorized || pcUatEffectiveRdnToken || pcUatHasRdnContext);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Legacy card metrics
  const [cards, setCards] = useState({
    totalPatients: 0,
    active48h: 0,
    nudgesToday: 0,
    positiveToday: 0,
    driftAlerts: 0,
    safetyAlerts: 0,
    influencedQueue: 0,
  });

// New org-level analytics payload (v2)
const [orgMetricsV2, setOrgMetricsV2] = useState(null);

useEffect(() => {
  if (!authorized || !baseUrl) return;

  setLoading(true);
  setErr(null);

  // RDN Console dashboard uses:
  //   - /console/metrics  (org / panel metrics, RDN-scoped)
  //   - /api/logs/history (temporary stand-in for influenced follow-ups queue)
  //   - /console/alerts   (drift + safety alerts, RDN-scoped)
  const headers = buildKeyrdHeaders({
    token,
    registryVersion,
    registryHash,
    extra: { Accept: "application/json" },
  });

  const readJson = async (res, label) => {
    const text = await res.text();
    let json = null;

    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      throw new Error(`${label} non-JSON (${res.status}): ${text.slice(0, 120)}…`);
    }

    if (!res.ok) {
      const msg = json?.error || json?.reason || `HTTP ${res.status}`;
      throw new Error(`${label} ${msg}`);
    }

    return json;
  };

  Promise.all([
    fetch(`${baseUrl}/console/metrics`, { headers }).then((r) => readJson(r, "metrics")),
    fetch(`${baseUrl}/api/logs/history?limit=200`, { headers }).then((r) => readJson(r, "history")),
    fetch(`${baseUrl}/console/alerts`, { headers }).then((r) => readJson(r, "alerts")),
  ])
    .then(([metricsJson, historyJson, alertsJson]) => {
      // Support both old and new shapes:
      //  • old: { ok, scope, metrics: {...} }
      //  • new: { ok, scope, legacy: {...}, v2: {...} }
      const legacyMetrics = metricsJson?.legacy || metricsJson?.metrics || {};
      const v2 = metricsJson?.v2 || null;

      // History endpoint shape:
      //   - /api/patient/history returns { ok:true, history:[...] }
      //   - /api/logs/history returns { ok:true, history:[...], insights?... } (or similar)
      const historyRows = Array.isArray(historyJson?.history)
        ? historyJson.history
        : Array.isArray(historyJson?.rows)
        ? historyJson.rows
        : Array.isArray(historyJson?.items)
        ? historyJson.items
        : [];

      const influencedQueueCount = historyRows.length;

      // Alerts endpoint shape:
      const alertsList = Array.isArray(alertsJson?.alerts)
        ? alertsJson.alerts
        : Array.isArray(alertsJson)
        ? alertsJson
        : [];

      const driftAlerts = alertsList.filter(
        (a) => a && (a.kind || a.type) === "drift_bm_anomaly"
      ).length;

      const safetyAlerts = alertsList.filter(
        (a) =>
          a &&
          ((a.kind || a.type) === "safety_freeze" ||
            (a.kind || a.type) === "registry_mismatch")
      ).length;

      setCards({
        totalPatients: toNumberOrZero(
          legacyMetrics.total_patients ?? legacyMetrics.totalPatients
        ),
        active48h: toNumberOrZero(
          legacyMetrics.active_patients ?? legacyMetrics.active48h
        ),
        nudgesToday: toNumberOrZero(
          legacyMetrics.total_nudges ?? legacyMetrics.nudgesToday
        ),
        positiveToday: toNumberOrZero(
          legacyMetrics.total_feedback ?? legacyMetrics.positiveToday
        ),
        driftAlerts,
        safetyAlerts,
        influencedQueue: influencedQueueCount,
      });

      setOrgMetricsV2(v2 || null);
    })
    .catch((e) => {
      setErr(e?.message || String(e));
      setOrgMetricsV2(null);
    })
    .finally(() => setLoading(false));
}, [pcUatDashboardAuthorized, baseUrl, pcUatEffectiveRdnToken, registryVersion, registryHash]);


  return (
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
        Dashboard
      </h1>
      <p style={{ fontSize: 13, color: "#4b5563", marginBottom: 16 }}>
        Global snapshot of patients, activity, and safety for the current KeyRD
        pilot (scoped to your RDN / org).
      </p>

      {!pcUatDashboardAuthorized && (
        <div
          style={{
            padding: 12,
            borderRadius: 8,
            border: "1px solid #f97316",
            backgroundColor: "#fffbeb",
            fontSize: 13,
          }}
        >
          Paste a valid bearer token in the top bar to load the RDN dashboard.
        </div>
      )}

      {pcUatDashboardAuthorized && loading && <div>Loading dashboard…</div>}

      {pcUatDashboardAuthorized && err && (
        <div
          style={{
            marginTop: 8,
            padding: 8,
            borderRadius: 6,
            border: "1px solid #fecaca",
            backgroundColor: "#fef2f2",
            fontSize: 12,
            color: "#b91c1c",
          }}
        >
          Error loading dashboard: {err}
        </div>
      )}

      {pcUatDashboardAuthorized && !loading && !err && (
        <>
          {/* Band 0 — existing legacy summary cards (no regression) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 16,
              marginTop: 16,
            }}
          >
            <MetricCard
              title="Total patients"
              value={cards.totalPatients}
              subtitle="Enrolled under your org"
            />
            <MetricCard
              title="Active patients"
              value={cards.active48h}
              subtitle="Crude active count (RDN scope)"
            />
            <MetricCard
              title="Total nudges"
              value={cards.nudgesToday}
              subtitle="All-time nudges in scope"
            />
            <MetricCard
              title="Total feedback"
              value={cards.positiveToday}
              subtitle="All-time responses in scope"
            />
            <MetricCard
              title="Drift alerts"
              value={cards.driftAlerts}
              subtitle="Behavioral drift anomalies"
            />
            <MetricCard
              title="Safety / registry alerts"
              value={cards.safetyAlerts}
              subtitle="Safety or registry freezes"
            />
            <MetricCard
              title="Influenced-follow-up queue"
              value={cards.influencedQueue}
              subtitle="Visits meeting influenced criteria"
            />
          </div>

          {/* All advanced org analytics are gated on v2 being present */}
          {orgMetricsV2 && (
            <>
              {/* Band A – Org Health Beam */}
              <section style={{ marginTop: 24 }}>
                <OrgHealthStrip orgHealth={orgMetricsV2.org_health} />
              </section>

              {/* Band B – Timing heatmap + NCPT impact table */}
              <section
                style={{
                  marginTop: 24,
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1fr)",
                  gap: 16,
                }}
              >
                <TimingHeatmap heatmap={orgMetricsV2.timing_heatmap} />
                <NcptImpactMatrix matrix={orgMetricsV2.ncpt_matrix} />
              </section>

              {/* Band C – Influenced events + risk radar */}
              <section
                style={{
                  marginTop: 24,
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 0.9fr)",
                  gap: 16,
                }}
              >
                <InfluencedEventsFeed events={orgMetricsV2.influenced_events} />
                <RiskFreezeRadar radar={orgMetricsV2.risk_radar} />
              </section>

              {/* Band D – Narrative summary */}
              <section style={{ marginTop: 24 }}>
                <OrgNarrativePanel narrative={orgMetricsV2.narrative} />
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Metric cards (legacy summary row)                                 */
/* ------------------------------------------------------------------ */

function MetricCard({ title, value, subtitle }) {
  return (
    <div
      style={{
        minWidth: 200,
        padding: 14,
        borderRadius: 10,
        backgroundColor: "#ffffff",
        boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
        border: "1px solid #e5e7eb",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
        {title}
      </div>
      <div style={{ fontSize: 28, fontWeight: 600, marginBottom: 4 }}>
        {value}
      </div>
      {subtitle && (
        <div style={{ fontSize: 11, color: "#6b7280" }}>{subtitle}</div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Org Health Strip                                                  */
/* ------------------------------------------------------------------ */

function OrgHealthStrip({ orgHealth }) {
  if (!orgHealth) return null;

  const engagement = toNumberOrZero(orgHealth.engagement_score);
  const safety = toNumberOrZero(orgHealth.safety_score);
  const drift = toNumberOrZero(orgHealth.drift_index);
  const adherenceVelocity = Number(orgHealth.adherence_velocity ?? 0);
  const efficiency = orgHealth.nudge_efficiency ?? null;

  function velocityLabel(v) {
    if (v > 0.2) return "Rising";
    if (v > 0.05) return "Gently rising";
    if (v < -0.2) return "Falling";
    if (v < -0.05) return "Gently falling";
    return "Flat";
  }

  function velocityArrow(v) {
    if (v > 0.05) return "↑";
    if (v < -0.05) return "↓";
    return "→";
  }

  const velPct = Math.round(Math.min(Math.max(adherenceVelocity, -1), 1) * 100);

  return (
    <div
      style={{
        padding: 16,
        borderRadius: 12,
        backgroundColor: "#f9fafb",
        border: "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          marginBottom: 8,
        }}
      >
        Org Health (KeyRD pilot)
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 12,
        }}
      >
        <OrgHealthPill
          label="Engagement"
          value={`${Math.round(engagement)} / 100`}
          help="How often your patients are reacting to nudges."
        />
        <OrgHealthPill
          label="Safety"
          value={`${Math.round(safety)} / 100`}
          help="Registry alignment, tone safety, and clinical freezes."
        />
        <OrgHealthPill
          label="Drift"
          value={`${Math.round(drift)} / 100`}
          help="Stability of behavior patterns over the last month."
        />
        <OrgHealthPill
          label="Adherence velocity"
          value={`${velocityArrow(adherenceVelocity)} ${velocityLabel(
            adherenceVelocity
          )} (${velPct}%)`}
          help="Whether response rates are trending up or down."
        />
        <OrgHealthPill
          label="Nudge efficiency"
          value={
            efficiency != null
              ? `${(efficiency * 100).toFixed(1)}%`
              : "—"
          }
          help="Responses per nudge sent in the last 28 days."
        />
      </div>
    </div>
  );
}

function OrgHealthPill({ label, value, help }) {
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 10,
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
      }}
      title={help}
    >
      <div
        style={{
          fontSize: 11,
          color: "#6b7280",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Timing Heatmap (summary-style)                                    */
/* ------------------------------------------------------------------ */

function TimingHeatmap({ heatmap }) {
  const bins = Array.isArray(heatmap?.bins) ? heatmap.bins : [];

  if (!bins.length) {
    return (
      <div
        style={{
          padding: 16,
          borderRadius: 12,
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
          When your patients respond
        </div>
        <div style={{ fontSize: 11, color: "#6b7280" }}>
          Response rate by day of week and hour of day.
        </div>
        <div style={{ fontSize: 12, marginTop: 12, color: "#6b7280" }}>
          Not enough data yet to find timing patterns.
        </div>
      </div>
    );
  }

  // Find the top 5 (day, hour) bins by response_rate
  const topBins = [...bins]
    .filter((b) => (b.response_rate ?? 0) > 0)
    .sort((a, b) => (b.response_rate ?? 0) - (a.response_rate ?? 0))
    .slice(0, 5);

  return (
    <div
      style={{
        padding: 16,
        borderRadius: 12,
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        When your patients respond
      </div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>
        Response rate by day of week and hour of day.
      </div>

      <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 12 }}>
        {topBins.map((b, idx) => (
          <li
            key={`${b.dow}-${b.hour}-${idx}`}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "4px 0",
            }}
          >
            <span>
              {formatDow(b.dow)} &middot; {formatHour(b.hour)}
            </span>
            <span>
              {(b.response_rate * 100).toFixed(0)}%{" "}
              <span style={{ color: "#6b7280" }}>
                ({b.responses ?? 0} / {b.nudges ?? b.nudges_sent ?? 0})
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatDow(d) {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  if (typeof d !== "number" || d < 0 || d > 6) return "—";
  return days[d];
}

function formatHour(h) {
  if (typeof h !== "number" || h < 0 || h > 23) return "—";
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:00 ${ampm}`;
}

/* ------------------------------------------------------------------ */
/*  NCPT Impact Matrix                                                */
/* ------------------------------------------------------------------ */

function NcptImpactMatrix({ matrix }) {
  const rows = Array.isArray(matrix?.rows) ? matrix.rows : [];

  return (
    <div
      style={{
        padding: 16,
        borderRadius: 12,
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        Which interventions work for which problems
      </div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>
        NCPT problems mapped to their most effective nudge families.
      </div>

      {!rows.length ? (
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          NCPT-linked outcomes will appear here once patients have diagnoses
          and enough nudge history.
        </div>
      ) : (
        <div
          style={{
            marginTop: 4,
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            overflow: "hidden",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 11,
            }}
          >
            <thead
              style={{
                backgroundColor: "#f9fafb",
                textAlign: "left",
              }}
            >
              <tr>
                <th style={thCell}>NCPT</th>
                <th style={thCell}>Patients</th>
                <th style={thCell}>Dominant target</th>
                <th style={thCell}>Best template</th>
                <th style={thCell}>Success</th>
                <th style={thCell}>Drift</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 8).map((r, idx) => (
                <tr
                  key={`${r.ncpt_code}-${idx}`}
                  style={{
                    borderTop: "1px solid #e5e7eb",
                  }}
                >
                  <td style={tdCell}>
                    <span style={{ fontWeight: 600 }}>{r.ncpt_code}</span>
                    {r.ncpt_label && (
                      <span style={{ color: "#6b7280", marginLeft: 4 }}>
                        {r.ncpt_label}
                      </span>
                    )}
                  </td>
                  <td style={tdCell}>{r.patients_with_code ?? 0}</td>
                  <td style={tdCell}>{r.dominant_behavior_target || "—"}</td>
                  <td style={tdCell}>{r.best_template_id || "—"}</td>
                  <td style={tdCell}>
                    {r.success_rate != null
                      ? `${(r.success_rate * 100).toFixed(0)}%`
                      : "—"}
                  </td>
                  <td style={tdCell}>
                    {r.drift_flag ? (
                      <span style={{ color: "#b91c1c", fontWeight: 600 }}>
                        drifting
                      </span>
                    ) : (
                      <span style={{ color: "#16a34a" }}>stable</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thCell = {
  padding: "6px 8px",
  fontWeight: 600,
  fontSize: 11,
  color: "#374151",
};

const tdCell = {
  padding: "6px 8px",
  verticalAlign: "top",
};

/* ------------------------------------------------------------------ */
/*  Influenced Events Feed                                            */
/* ------------------------------------------------------------------ */

function InfluencedEventsFeed({ events }) {
  const items = Array.isArray(events) ? events : [];

  return (
    <div
      style={{
        padding: 16,
        borderRadius: 12,
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        Nudges that changed behavior
      </div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>
        Recent visits where KeyRD nudges met the “influenced follow-up” criteria.
      </div>

      {!items.length ? (
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          Influenced behavior events will appear here as your pilot accumulates
          more visits and responses.
        </div>
      ) : (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            fontSize: 12,
          }}
        >
          {items.slice(0, 10).map((e, idx) => (
            <li
              key={`${e.timestamp}-${idx}`}
              style={{
                padding: "6px 0",
                borderTop: idx === 0 ? "none" : "1px solid #e5e7eb",
              }}
            >
              <div style={{ color: "#6b7280", fontSize: 11 }}>
                {formatDateTime(e.timestamp)} · {e.patient_label || "Patient"}
              </div>
              <div>
                {e.behavior_type || "behavior"}{" "}
                {e.direction === "improved"
                  ? "improved"
                  : e.direction === "worsened"
                  ? "worsened"
                  : "changed"}{" "}
                after {e.nudge_family || "a nudge"}
                {e.ncpt_code ? ` (NCPT ${e.ncpt_code})` : ""}.
              </div>
              {typeof e.confidence === "number" && (
                <div style={{ fontSize: 11, color: "#6b7280" }}>
                  Confidence: {(e.confidence * 100).toFixed(0)}%
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatDateTime(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  const date = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${date}, ${time}`;
}

/* ------------------------------------------------------------------ */
/*  Risk & Freeze Radar                                               */
/* ------------------------------------------------------------------ */

function RiskFreezeRadar({ radar }) {
  const r = radar || {};

  return (
    <div
      style={{
        padding: 16,
        borderRadius: 12,
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
        height: "100%",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        Safety & registry overview
      </div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>
        Freezes, registry mismatches, and safety events across your org.
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 8,
          fontSize: 12,
        }}
      >
        <RadarMetric
          label="Active freezes"
          value={r.active_freezes}
          highlight={r.clinical_freezes > 0}
        />
        <RadarMetric
          label="Clinical freezes"
          value={r.clinical_freezes}
          highlight={r.clinical_freezes > 0}
        />
        <RadarMetric
          label="Technical freezes"
          value={r.technical_freezes}
        />
        <RadarMetric
          label="Registry mismatches (7d)"
          value={r.registry_mismatches_last_7d}
          highlight={r.registry_mismatches_last_7d > 0}
        />
        <RadarMetric
          label="Safety events (30d)"
          value={r.safety_events_last_30d}
          highlight={r.safety_events_last_30d > 0}
        />
        <RadarMetric
          label="High-risk patients"
          value={r.high_risk_patients}
          highlight={r.high_risk_patients > 0}
        />
      </div>

      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 10 }}>
        Any non-zero clinical freeze or registry mismatch should be reviewed.
      </div>
    </div>
  );
}

function RadarMetric({ label, value, highlight }) {
  const v = toNumberOrZero(value);
  const color = highlight ? "#b91c1c" : "#111827";

  return (
    <div>
      <div style={{ fontSize: 11, color: "#6b7280" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color }}>{v}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Org Narrative Panel                                               */
/* ------------------------------------------------------------------ */

function OrgNarrativePanel({ narrative }) {
  if (!narrative) return null;

  const summary = narrative.summary || "";
  const points = Array.isArray(narrative.talking_points)
    ? narrative.talking_points
    : [];

  return (
    <div
      style={{
        padding: 16,
        borderRadius: 12,
        backgroundColor: "#f9fafb",
        border: "1px solid #e5e7eb",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        This week in your pilot
      </div>
      {summary && (
        <p
          style={{
            fontSize: 12,
            color: "#111827",
            marginTop: 4,
            marginBottom: 8,
          }}
        >
          {summary}
        </p>
      )}
      {points.length > 0 && (
        <ul
          style={{
            fontSize: 12,
            color: "#374151",
            paddingLeft: 18,
            margin: 0,
          }}
        >
          {points.map((p, idx) => (
            <li key={idx} style={{ marginBottom: 2 }}>
              {p}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default Dashboard;
